import { useDisclosure } from '@chakra-ui/react'
import { IconButton, Menu, MenuButton, MenuItem, MenuList } from '@missionlabs/react'
import { BlockIcon, UserCircleIconLight } from '@missionlabs/react/circleloop'
import { DotsVerticalIconRegular } from '@missionlabs/react/zeta'
import { DirectoryEntry, UserRole } from '@missionlabs/types'
import { isInternalContact } from '@missionlabs/utils'
import { BlockContactDialog } from 'features/contacts/BlockContactDialog'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFormatToNumberE164 } from 'shared/hooks/useFormatToNumberE164'
import { useUserRole } from 'shared/hooks/useUserRole'

interface ActivityFeedHeaderMenuProps {
    // For contacts:
    contact?: DirectoryEntry
    // For unsaved contacts:
    contactNumber?: string
    onAddToContact?: () => void
}

export const ActivityFeedHeaderMenu = ({
    contact,
    contactNumber,
    onAddToContact
}: ActivityFeedHeaderMenuProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const role = useUserRole()
    const { formatToLocalNumber } = useFormatToNumberE164()
    const blockDialog = useDisclosure()
    const [numberToBlock, setNumberToBlock] = useState('')

    const blockableNumbers = useMemo(() => {
        if (role !== UserRole.ADMIN) return []
        if (!contact) return contactNumber ? [contactNumber] : []
        if (isInternalContact(contact)) return []
        return contact.phoneNumbers?.map(number => number.numberE164) ?? []
    }, [role, contact, contactNumber])

    function handleBlock(numberE164: string) {
        setNumberToBlock(numberE164)
        blockDialog.onOpen()
    }

    return (
        <Menu>
            <MenuButton
                as={IconButton}
                variant="ghost"
                aria-label="More"
                data-testid="activity-header-menu-trigger"
            >
                <DotsVerticalIconRegular />
            </MenuButton>
            <MenuList data-testid="activity-header-menu">
                {!contact && contactNumber && (
                    <>
                        <MenuItem
                            icon={<UserCircleIconLight />}
                            onClick={() => {
                                navigate({
                                    pathname: '/contacts/add',
                                    search: `numberE164=${contactNumber}`
                                })
                            }}
                        >
                            {t('contacts.createContact')}
                        </MenuItem>
                        <MenuItem icon={<UserCircleIconLight />} onClick={onAddToContact}>
                            {t('contacts.addToExisting')}
                        </MenuItem>
                    </>
                )}
                {contact && (
                    <MenuItem
                        icon={<UserCircleIconLight />}
                        onClick={() => navigate(`/contacts/${contact.ID}`)}
                    >
                        {t('contacts.viewContact')}
                    </MenuItem>
                )}
                {blockableNumbers.map(numberE164 => (
                    <MenuItem
                        key={numberE164}
                        variant="destructive"
                        icon={<BlockIcon />}
                        onClick={() => handleBlock(numberE164)}
                    >
                        {blockableNumbers.length > 1
                            ? `${t('blockedNumbers.blockNumber')} ${formatToLocalNumber(
                                  numberE164
                              )}`
                            : t('blockedNumbers.blockNumber')}
                    </MenuItem>
                ))}
            </MenuList>
            {blockDialog.isOpen && (
                <BlockContactDialog
                    numberE164={numberToBlock}
                    isOpen={blockDialog.isOpen}
                    onClose={blockDialog.onClose}
                />
            )}
        </Menu>
    )
}
